import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import findLast from 'lodash/findLast'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import type { AppSetting } from '@ztechapps/common.contract'
import { buildGraphqlQuery, getWindow } from '@ztechapps/common.utils'
import { useAppSetting, useMutation, useUser } from '@ztechapps/common.hook'
import type { ShortcutConfig, ShortcutDefinition } from './KeyboardShortcutContext'
import { KeyboardShortcutContext } from './KeyboardShortcutContext'

// region graphql
const documentFragment = `
    _id
    key
    value
    scope { key name }
    `

const queries = buildGraphqlQuery('AppSetting', documentFragment)

const createConfig = {
  ...queries.createConfig,
  options: {
    mutationOptions: {
      refetchQueries: ['allAppSetting'],
    },
  },
}
// endregion

// region key matching helpers
export const parseKeyCombo = (
  keys: string
): { key: string; ctrl: boolean; shift: boolean; alt: boolean; meta: boolean } => {
  const parts = keys.toLowerCase().split('+')
  return {
    ctrl: parts.includes('ctrl'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
    meta: parts.includes('meta') || parts.includes('cmd'),
    key: parts.filter((p) => !['ctrl', 'shift', 'alt', 'meta', 'cmd'].includes(p))[0] || '',
  }
}

export const matchesKeyEvent = (keys: string, event: KeyboardEvent): boolean => {
  if (!keys) return false

  const combo = parseKeyCombo(keys)

  return (
    event.ctrlKey === combo.ctrl &&
    event.shiftKey === combo.shift &&
    event.altKey === combo.alt &&
    event.metaKey === combo.meta &&
    event.key.toLowerCase() === combo.key
  )
}
// endregion

const isEditableElement = (element: Element | null): boolean => {
  if (!element) return false
  const tagName = element.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true
  if (element.getAttribute('contenteditable') === 'true') return true
  return false
}

const noop = () => undefined
const DEFAULT_REGISTRANT = ''

type Registration = { registrant: string; shortcut: ShortcutDefinition }

// The newest enabled registration owns the key; with none enabled the newest one stands, so a
// disabled shortcut still swallows its key instead of falling through to nothing.
const pickActive = (stack: Registration[]): ShortcutDefinition | undefined =>
  (findLast(stack, (r) => r.shortcut.enabled !== false) ?? last(stack))?.shortcut
const EMPTY_BINDINGS: Record<string, string> = {}

export type KeyboardShortcutProviderProps = {
  children: React.ReactNode
  appSettingKey: string
  defaultShortcuts: ShortcutConfig[]
}

export const KeyboardShortcutProvider = ({
  children,
  appSettingKey,
  defaultShortcuts,
}: KeyboardShortcutProviderProps) => {
  const { user } = useUser()
  const [renderCount, setRenderCount] = useState(0)
  const pausedRef = useRef(false)
  // What the keydown listener and the settings UI read: one definition per id.
  const shortcutsRef = useRef<Map<string, ShortcutDefinition>>(
    new Map(defaultShortcuts.map((s) => [s.id, { ...s, action: noop }]))
  )
  // Every mounted registrant per id, oldest first. Two components may register the same id at once
  // (a page's Refresh beside a layout's), so `shortcutsRef` alone cannot tell whose entry to drop.
  const registrationsRef = useRef<Map<string, Registration[]>>(new Map())

  const userId = user?._id

  const filter = useMemo(
    () => ({
      key: appSettingKey,
      'scope.key': 'user',
      'scope.name': userId,
    }),
    [appSettingKey, userId]
  )

  const { setting, loading } = useAppSetting({ filter, appSettingKey: undefined })

  const [onUpdate] = useMutation<AppSetting, Partial<AppSetting>>(queries.updateConfig)
  const [onCreate] = useMutation(createConfig)

  const userBindings: Record<string, string> = useMemo(() => {
    if (isEmpty(setting?.value)) return EMPTY_BINDINGS
    try {
      return JSON.parse(setting.value) as Record<string, string>
    } catch {
      return EMPTY_BINDINGS
    }
  }, [setting?.value])

  const getResolvedKeys = useCallback(
    (id: string): string => {
      if (id in userBindings) return userBindings[id]
      return shortcutsRef.current.get(id)?.keys || ''
    },
    [userBindings]
  )

  const saveBindings = useCallback(
    async (bindings: Record<string, string>) => {
      const value = JSON.stringify(bindings)
      if (setting?._id) {
        await onUpdate({ input: { _id: setting._id, value } })
      } else {
        await onCreate({
          input: {
            key: appSettingKey,
            'scope.key': 'user',
            'scope.name': userId,
            value,
          },
        })
      }
    },
    [setting, onUpdate, onCreate, appSettingKey, userId]
  )

  const updateBinding = useCallback(
    async (id: string, keys: string) => {
      const newBindings = { ...userBindings, [id]: keys }
      await saveBindings(newBindings)
    },
    [userBindings, saveBindings]
  )

  const removeBinding = useCallback(
    async (id: string) => {
      const newBindings = { ...userBindings, [id]: '' }
      await saveBindings(newBindings)
    },
    [userBindings, saveBindings]
  )

  const reassignBinding = useCallback(
    async (fromId: string, toId: string, keys: string) => {
      const newBindings = { ...userBindings, [fromId]: '', [toId]: keys }
      await saveBindings(newBindings)
    },
    [userBindings, saveBindings]
  )

  const clearAllBindings = useCallback(async () => {
    await saveBindings({})
  }, [saveBindings])

  const registerShortcut = useCallback((shortcut: ShortcutDefinition, registrant: string = DEFAULT_REGISTRANT) => {
    const stack = registrationsRef.current.get(shortcut.id) ?? []
    const own = stack.findIndex((r) => r.registrant === registrant)
    if (own === -1) stack.push({ registrant, shortcut })
    else stack[own] = { registrant, shortcut }
    registrationsRef.current.set(shortcut.id, stack)

    const isNewId = !shortcutsRef.current.has(shortcut.id)
    shortcutsRef.current.set(shortcut.id, pickActive(stack) ?? shortcut)
    if (isNewId) setRenderCount((c) => c + 1)
  }, [])

  const unregisterShortcut = useCallback((id: string, registrant: string = DEFAULT_REGISTRANT) => {
    const stack = (registrationsRef.current.get(id) ?? []).filter((r) => r.registrant !== registrant)
    registrationsRef.current.set(id, stack)

    const active = pickActive(stack)
    const existing = shortcutsRef.current.get(id)
    if (active) shortcutsRef.current.set(id, active)
    else if (existing) shortcutsRef.current.set(id, { ...existing, action: noop })
  }, [])

  // Global keydown listener
  useEffect(() => {
    const win = getWindow()
    if (!win) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (pausedRef.current) return
      const inEditable = isEditableElement(win.document.activeElement)

      for (const [, shortcut] of shortcutsRef.current) {
        const resolvedKeys = shortcut.id in userBindings ? userBindings[shortcut.id] : shortcut.keys || ''
        if (!resolvedKeys) continue

        if (matchesKeyEvent(resolvedKeys, event)) {
          if (inEditable && !event.ctrlKey && !event.metaKey && !event.altKey) return
          if (shortcut.enabled === false) return
          event.preventDefault()
          event.stopPropagation()
          shortcut.action()
          return
        }
      }
    }

    win.addEventListener('keydown', handleKeyDown)
    return () => win.removeEventListener('keydown', handleKeyDown)
  }, [userBindings])

  const contextValue = useMemo(
    () => ({
      shortcuts: new Map(shortcutsRef.current),
      userBindings,
      registerShortcut,
      unregisterShortcut,
      updateBinding,
      removeBinding,
      reassignBinding,
      clearAllBindings,
      getResolvedKeys,
      setPaused: (paused: boolean) => {
        pausedRef.current = paused
      },
      loading,
    }),
    // `renderCount` is a deliberate recompute trigger: `shortcuts: new Map(shortcutsRef.current)`
    // reads a ref, which React does not track, so `registerShortcut`'s `setRenderCount` bump
    // is the only signal this memo has that the ref-backed map actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userBindings,
      registerShortcut,
      unregisterShortcut,
      updateBinding,
      removeBinding,
      reassignBinding,
      clearAllBindings,
      getResolvedKeys,
      loading,
      renderCount,
    ]
  )

  return <KeyboardShortcutContext.Provider value={contextValue}>{children}</KeyboardShortcutContext.Provider>
}
